import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { HaberlerGrid } from '../components/Haberler';
import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import { sliceStaticNewsListPage } from '../data/staticSite/content';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { mapNewsFallbackFromI18n } from '../data/newsItems';

const NEWS_PAGE_SIZE = 9;
const PAGE_QUERY_KEY = 'page';

/**
 * @param {{ statusLabel: string }} props
 */
function NewsListPageSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <ul className="haberler-grid list-unstyled row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-0">
                    {Array.from({ length: NEWS_PAGE_SIZE }, (_, i) => (
                        <li
                            key={`news-sk-${i}`}
                            className="col d-flex"
                            aria-hidden
                        >
                            <article className="haber-card haber-card--skeleton w-100 h-100 d-flex flex-column rounded-4 overflow-hidden">
                                <div className="haber-card-image-wrap position-relative">
                                    <div className="haber-card-image ratio ratio-16x9">
                                        <Skeleton
                                            className="haber-card-skeleton-media"
                                            height="100%"
                                            borderRadius={0}
                                        />
                                    </div>
                                    <Skeleton
                                        height={34}
                                        width={100}
                                        borderRadius={999}
                                        className="haber-card-skeleton-date"
                                    />
                                </div>
                                <div className="haber-card-body d-flex flex-column flex-grow-1 px-4 pt-4 pb-4">
                                    <Skeleton
                                        height={22}
                                        width="92%"
                                        className="mb-3"
                                        borderRadius={8}
                                    />
                                    <Skeleton
                                        height={13}
                                        className="mb-2"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={13}
                                        className="mb-2"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={13}
                                        width="75%"
                                        className="mb-4"
                                        borderRadius={6}
                                    />
                                    <hr className="haber-card-divider my-0 mb-3 opacity-100" />
                                    <Skeleton
                                        height={40}
                                        width={160}
                                        borderRadius={999}
                                    />
                                </div>
                            </article>
                        </li>
                    ))}
                </ul>
            </SkeletonTheme>
        </div>
    );
}

/**
 * Haberler listesi — URL: /news?page=2 (sayfa başına 9 kayıt)
 */
export function NewsListPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState(
        USE_STATIC_SITE_CONTENT
            ? sliceStaticNewsListPage(1, NEWS_PAGE_SIZE).items
            : null,
    );
    const [meta, setMeta] = useState(
        USE_STATIC_SITE_CONTENT
            ? sliceStaticNewsListPage(1, NEWS_PAGE_SIZE).meta
            : null,
    );

    const fallbackItems = useMemo(() => {
        const raw = t('newsList.fallbackItems', { returnObjects: true });
        return mapNewsFallbackFromI18n(raw);
    }, [t]);

    const pageFromUrl = Math.max(
        1,
        parseInt(searchParams.get(PAGE_QUERY_KEY) || '1', 10) || 1,
    );

    const goToPage = useCallback(
        (nextPage) => {
            const p = Math.max(1, nextPage);
            if (p <= 1) {
                setSearchParams({}, { replace: true });
            } else {
                setSearchParams({ [PAGE_QUERY_KEY]: String(p) }, { replace: true });
            }
        },
        [setSearchParams],
    );

    useEffect(() => {
        if (USE_STATIC_SITE_CONTENT) {
            const { items: nextItems, meta: nextMeta } = sliceStaticNewsListPage(
                pageFromUrl,
                NEWS_PAGE_SIZE,
            );
            setItems(nextItems);
            setMeta(nextMeta);
            return undefined;
        }

        let cancelled = false;

        /*
        (async () => {
            try {
                const { items: nextItems, meta: nextMeta } =
                    await fetchCmsNewsPaged({
                        pageNumber: pageFromUrl,
                        pageSize: NEWS_PAGE_SIZE,
                    });
                if (cancelled) return;
                setItems(nextItems);
                setMeta(nextMeta);
            } catch {
                if (cancelled) return;
                const all = fallbackItems;
                const totalPages = Math.max(
                    1,
                    Math.ceil(all.length / NEWS_PAGE_SIZE),
                );
                const safePage = Math.min(pageFromUrl, totalPages);
                const start = (safePage - 1) * NEWS_PAGE_SIZE;
                setItems(all.slice(start, start + NEWS_PAGE_SIZE));
                setMeta({
                    totalCount: all.length,
                    totalPages,
                    currentPage: safePage,
                    pageSize: NEWS_PAGE_SIZE,
                    hasNext: safePage < totalPages,
                    hasPrevious: safePage > 1,
                });
                if (safePage !== pageFromUrl) {
                    goToPage(safePage);
                }
            }
        })();
        */

        return () => {
            cancelled = true;
        };
    }, [pageFromUrl, goToPage, fallbackItems]);

    useEffect(() => {
        if (meta && pageFromUrl > meta.totalPages && meta.totalPages >= 1) {
            goToPage(meta.totalPages);
        }
    }, [meta, pageFromUrl, goToPage]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [pageFromUrl]);

    const showPagination =
        meta != null && meta.totalPages > 1 && items != null && items.length > 0;

    return (
        <>
            <PageSeo
                title={`${t('newsList.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.newsList')}
            />
            <SubpageLayout
                title={t('newsList.title')}
                subtitle={t('newsList.subtitle')}
                contentClassName="news-list-page"
            >
            <div className="haberler-section haberler-section--grid news-list-page-inner bg-light rounded-4 px-3 px-md-4 py-4 py-md-5">
                {items == null ? (
                    <NewsListPageSkeleton
                        statusLabel={t('newsList.loading')}
                    />
                ) : items.length === 0 ? (
                    <p className="text-center text-secondary mb-0 py-5">
                        {t('newsList.empty')}
                    </p>
                ) : (
                    <>
                        <HaberlerGrid items={items} />
                        {showPagination && meta ? (
                            <nav
                                className="news-list-pagination d-flex flex-column align-items-center gap-3 mt-4 pt-2"
                                aria-label={t('newsList.paginationNavAria')}
                            >
                                <p className="news-list-pagination-summary mb-0">
                                    {t('newsList.paginationSummary', {
                                        count: meta.totalCount,
                                    })}
                                </p>
                                <ul className="news-list-pagination-pages list-unstyled mb-0 d-flex flex-wrap align-items-center justify-content-center gap-1 gap-sm-2">
                                    <li>
                                        <button
                                            type="button"
                                            className="news-list-pagination-btn news-list-pagination-btn--nav"
                                            disabled={!meta.hasPrevious}
                                            onClick={() =>
                                                goToPage(meta.currentPage - 1)
                                            }
                                            aria-label={t(
                                                'newsList.paginationPrevAria',
                                            )}
                                        >
                                            <ChevronLeft
                                                size={22}
                                                strokeWidth={2}
                                                aria-hidden
                                            />
                                        </button>
                                    </li>
                                    {meta.totalPages <= 12
                                        ? Array.from(
                                              {
                                                  length: meta.totalPages,
                                              },
                                              (_, i) => i + 1,
                                          ).map((n) => (
                                              <li key={n}>
                                                  <button
                                                      type="button"
                                                      className={`news-list-pagination-btn${n === meta.currentPage ? ' is-active' : ''}`}
                                                      onClick={() =>
                                                          goToPage(n)
                                                      }
                                                      aria-label={t(
                                                          'newsList.paginationPageAria',
                                                          { page: n },
                                                      )}
                                                      aria-current={
                                                          n ===
                                                          meta.currentPage
                                                              ? 'page'
                                                              : undefined
                                                      }
                                                  >
                                                      {n}
                                                  </button>
                                              </li>
                                          ))
                                        : null}
                                    {meta.totalPages > 12 ? (
                                        <li>
                                            <span
                                                className="news-list-pagination-indicator"
                                                aria-current="page"
                                            >
                                                {meta.currentPage} /{' '}
                                                {meta.totalPages}
                                            </span>
                                        </li>
                                    ) : null}
                                    <li>
                                        <button
                                            type="button"
                                            className="news-list-pagination-btn news-list-pagination-btn--nav"
                                            disabled={!meta.hasNext}
                                            onClick={() =>
                                                goToPage(meta.currentPage + 1)
                                            }
                                            aria-label={t(
                                                'newsList.paginationNextAria',
                                            )}
                                        >
                                            <ChevronRight
                                                size={22}
                                                strokeWidth={2}
                                                aria-hidden
                                            />
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        ) : null}
                    </>
                )}
            </div>
        </SubpageLayout>
        </>
    );
}
